/**
 * MarkdownFieldEditModal - Lightweight modal for editing markdown fields
 * Uses TipTap WYSIWYG editor for rich text editing
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './dialog';
import { Button } from './button';
import { MarkdownEditor } from './markdown-editor';
import { Loader2, AlertTriangle } from 'lucide-react';

const MarkdownFieldEditModal = ({
  isOpen,
  onClose,
  onSave,
  label,
  fieldName,
  value,
  placeholder = 'Start writing...',
}) => {
  const [editValue, setEditValue] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Reset edit value when modal opens with new value
  useEffect(() => {
    if (isOpen) {
      setEditValue(value || '');
      setShowDiscardConfirm(false);
    }
  }, [isOpen, value]);

  const hasChanges = editValue !== (value || '');

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(fieldName, editValue);
      onClose();
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  };

  const handleConfirmDiscard = () => {
    setShowDiscardConfirm(false);
    setEditValue(value || '');
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={() => {}}>
        <DialogContent 
          className="max-w-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700"
          data-testid={`markdown-edit-modal-${fieldName}`}
          hideCloseButton
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-gray-100">
              Edit {label}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <MarkdownEditor
                content={editValue}
                onChange={setEditValue}
                placeholder={placeholder}
                minHeight={300}
                className="bg-white dark:bg-gray-800"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={handleCancel}
              disabled={saving}
              data-testid={`markdown-edit-cancel-${fieldName}`}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              data-testid={`markdown-edit-save-${fieldName}`}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discard Changes Confirmation Dialog */}
      <Dialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <DialogContent className="max-w-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Discard Changes?
            </DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              You have unsaved changes. Are you sure you want to discard them?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowDiscardConfirm(false)}
            >
              Keep Editing
            </Button>
            <Button 
              variant="destructive"
              onClick={handleConfirmDiscard}
            >
              Discard Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export { MarkdownFieldEditModal };
